import * as React from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'

export interface SidebarButtonProps
	extends Omit<React.ComponentPropsWithoutRef<typeof Link>, 'to'> {
	label: string
	icon: React.ReactNode
	currentPage?: string
	href?: string
	collapsed?: boolean
	disabled?: boolean
}

const SidebarButton = React.forwardRef<HTMLAnchorElement, SidebarButtonProps>(
	({ label, icon, currentPage, href, collapsed, disabled, ...props }, ref) => {
		const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
			if (disabled) {
				e.preventDefault()
				e.stopPropagation()
				return
			}
			// Call original onClick if provided
			if (props.onClick) {
				props.onClick(e)
			}
		}

		// Create a more robust matching system
		const isActive = () => {
			if (!currentPage || !href) return false

			// Extract the path from href (e.g., "/ai-agent" -> "ai-agent")
			const hrefPath = href.replace('/', '')

			// Check if currentPage matches the href path
			return currentPage === hrefPath
		}

		if (disabled) {
			return (
				<span
					className={cn(
						'flex gap-2 p-2 rounded-lg truncate transition-all cursor-not-allowed',
						collapsed ? '' : 'pe-12',
						'pointer-events-none opacity-50'
					)}
					title={collapsed ? label : undefined}
					aria-disabled={true}
					tabIndex={-1}
				>
					<span className="backface-hidden">{icon}</span>
					{!collapsed && label}
				</span>
			)
		}

		return (
			<Link
				ref={ref}
				to={href || '#'}
				className={cn(
					'flex gap-2 p-2 rounded-lg truncate transition-all cursor-pointer active:scale-100 hover:scale-105 transition-all duration-300 ease-in-out',
					!isActive() && 'hover:bg-gray-100 hover:text-gray-900',
					isActive() && 'bg-purple-100 border border-purple-200 text-purple-700',
					collapsed ? '' : 'pe-12'
				)}
				onClick={handleClick}
				title={collapsed ? label : undefined}
				aria-current={isActive() ? 'page' : undefined}
				{...props}
			>
				<span className="backface-hidden">{icon}</span>
				{!collapsed && label}
			</Link>
		)
	}
)
SidebarButton.displayName = 'SidebarButton'

export { SidebarButton }
